import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plug, Trash2, Save, ExternalLink, Loader2, Check, X, Shield, BarChart3, Sparkles,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function IntegrationsSettings() {
  const [providers, setProviders] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // provider key
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null); // provider key

  const load = async () => {
    try {
      const [pr, us] = await Promise.all([
        axios.get(`${API}/api/settings/integrations`, { withCredentials: true }),
        axios.get(`${API}/api/settings/usage/ai-seo`, { withCredentials: true }),
      ]);
      setProviders(pr.data?.providers || []);
      setUsage(us.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settings");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.key);
    setFormValues(Object.fromEntries(p.fields.map((f) => [f.name, ""])));
  };

  const save = async (provider) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/settings/integrations`,
        { provider, values: formValues },
        { withCredentials: true },
      );
      toast.success("Integration saved & encrypted");
      setEditing(null);
      setFormValues({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (provider) => {
    try {
      await axios.delete(`${API}/api/settings/integrations/${provider}`, { withCredentials: true });
      toast.success("Disconnected");
      setConfirmRemove(null);
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="integrations-page" style={{ background: "#F8FAFC", minHeight: "100vh", color: "#0F172A" }}>
      <header style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 12px" }}>
        <span className="ai-seo-eyebrow"><Plug size={14} /> Integrations</span>
        <h1 style={{ fontSize: 34, margin: "8px 0 4px", fontWeight: 800 }}>Connect your API keys</h1>
        <p style={{ color: "#64748B", margin: 0, maxWidth: 700 }}>
          Bring your own keys for Serper, DataForSEO, OpenAI or Anthropic. We encrypt them at rest with
          Fernet (AES-128-CBC + HMAC) and never log the secret in plain text.
        </p>
      </header>

      {/* Usage summary */}
      {usage && (
        <section style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px" }}>
          <div data-testid="ai-seo-usage" style={{ marginTop: 16, padding: 16, borderRadius: 12, background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.22)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <BarChart3 size={20} color="#a78bfa" />
            <div style={{ flex: 1, minWidth: 240 }}>
              <strong>AI SEO toolkit usage today</strong>
              <div style={{ color: "#64748B", fontSize: 13 }}>
                {usage.unlimited ? (
                  <><Shield size={11} style={{ verticalAlign: -2 }} /> Super admin — unlimited</>
                ) : (
                  <>{usage.used} / {usage.limit} calls used · {usage.remaining} remaining today</>
                )}
              </div>
            </div>
            {!usage.unlimited && (
              <div style={{ width: 160, height: 8, borderRadius: 999, background: "#E2E8F0", overflow: "hidden" }}>
                <div style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%`, height: "100%", background: "linear-gradient(90deg, #a78bfa, #ec7c5e)" }} />
              </div>
            )}
            <small style={{ color: "#64748B" }}>resets {new Date(usage.resets_at).toLocaleTimeString()}</small>
          </div>
        </section>
      )}

      <main style={{ maxWidth: 1100, margin: "0 auto", padding: "20px 24px 60px", display: "grid", gap: 14 }}>
        {loading && <div data-testid="integrations-loading" className="ai-seo-eyebrow" style={{ alignSelf: "flex-start" }}><Loader2 size={12} className="animate-spin" /> Loading…</div>}
        {!loading && providers.map((p) => {
          const isOpen = editing === p.key;
          const on = p.status?.configured;
          return (
            <div key={p.key} data-testid={`integration-card-${p.key}`} style={{ padding: 18, borderRadius: 12, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16, display: "flex", gap: 8, alignItems: "center" }}>
                    {p.label}
                    {on ? (
                      <span data-testid={`integration-status-${p.key}`} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(74,222,128,0.16)", border: "1px solid rgba(74,222,128,0.35)", color: "#10B981" }}>
                        <Check size={10} style={{ verticalAlign: -1 }} /> Connected
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(148,163,184,0.12)", border: "1px solid rgba(148,163,184,0.25)", color: "#475569" }}>
                        <X size={10} style={{ verticalAlign: -1 }} /> Not connected
                      </span>
                    )}
                  </h3>
                  <small style={{ color: "#64748B" }}>
                    {on ? <>Saved {new Date(p.status.updated_at).toLocaleString()} · key {p.status.preview}</> : "No key on file"}
                  </small>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {on && (
                    <button onClick={() => setConfirmRemove(p.key)} className="studio-btn-ghost" data-testid={`integration-remove-${p.key}`} style={{ padding: "6px 12px", fontSize: 12 }}>
                      <Trash2 size={12} /> Disconnect
                    </button>
                  )}
                  <button
                    onClick={() => (isOpen ? setEditing(null) : startEdit(p))}
                    className="studio-btn-primary"
                    data-testid={`integration-edit-${p.key}`}
                    style={{ padding: "6px 14px", fontSize: 12 }}
                  >
                    {isOpen ? "Cancel" : on ? "Update key" : "Add key"}
                  </button>
                </div>
              </div>

              <ul style={{ marginTop: 12, paddingLeft: 18, color: "#475569", fontSize: 13 }}>
                {p.powers.map((line, i) => (
                  <li key={i} style={{ marginBottom: 2 }}><Sparkles size={11} style={{ verticalAlign: -1, color: "#a78bfa", marginRight: 6 }} />{line}</li>
                ))}
              </ul>
              {p.where_to_get && (
                <small style={{ display: "block", marginTop: 6 }}>
                  <ExternalLink size={11} style={{ verticalAlign: -1 }} />{" "}
                  <a href={p.where_to_get.split(" ")[0]} target="_blank" rel="noreferrer" style={{ color: "#a78bfa" }}>
                    Where to get this key — {p.where_to_get}
                  </a>
                </small>
              )}

              {isOpen && (
                <div data-testid={`integration-form-${p.key}`} style={{ marginTop: 14, padding: 14, borderRadius: 10, background: "rgba(0,0,0,0.25)" }}>
                  {p.fields.map((f) => (
                    <label key={f.name} style={{ display: "block", marginBottom: 10 }}>
                      <span style={{ display: "block", fontSize: 12, color: "#64748B", marginBottom: 4 }}>{f.label}</span>
                      <input
                        type={f.type}
                        value={formValues[f.name] || ""}
                        onChange={(e) => setFormValues((s) => ({ ...s, [f.name]: e.target.value }))}
                        data-testid={`integration-input-${p.key}-${f.name}`}
                        autoComplete="off"
                        placeholder={f.type === "password" ? "paste here — never shown again" : f.label}
                        style={{
                          width: "100%", padding: "10px 12px", borderRadius: 8,
                          background: "#F8FAFC", border: "1px solid #E2E8F0",
                          color: "#0F172A", fontSize: 14,
                        }}
                      />
                    </label>
                  ))}
                  <button
                    onClick={() => save(p.key)}
                    disabled={saving}
                    className="studio-btn-primary"
                    data-testid={`integration-save-${p.key}`}
                    style={{ padding: "8px 16px", fontSize: 13 }}
                  >
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save & encrypt
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </main>

      <AlertDialog open={!!confirmRemove} onOpenChange={(open) => !open && setConfirmRemove(null)}>
        <AlertDialogContent
          data-testid="integration-confirm-remove"
          className="bg-[#11172a] border border-white/10 text-[#e9ecf3]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#e9ecf3]">Disconnect this integration?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#9aa4b6]">
              Your encrypted credentials for <strong className="text-[#e9ecf3]">{confirmRemove}</strong> will be removed.
              Tools that depend on this key will revert to "Not connected" until you add it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-[#e9ecf3] hover:bg-white/10" data-testid="integration-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmRemove && remove(confirmRemove)}
              data-testid="integration-confirm-ok"
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              Yes, disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
